import { Clock } from 'lucide-react';
import { type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { Tag } from '@/components/ui/tag';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';

interface TimeZoneChipProps {
  /** The chip's text, naming the zone the dates on screen count in. */
  children: ReactNode;
  /** The opening's IANA zone, named in the tooltip. eg 'America/Chicago' */
  timeZone: string;
}

/** The opening's time zone, with why it applies on hover or keyboard focus. */
export function TimeZoneChip({ children, timeZone }: TimeZoneChipProps) {
  return (
    <Tooltip
      side="bottom"
      content={
        <div className="space-y-1">
          <p className="font-medium">
            <FormattedMessage
              defaultMessage="Why this time zone?"
              description="Title of the tooltip on the opening's time zone chip"
            />
          </p>
          <p>
            <FormattedMessage
              defaultMessage="Hiring goals, campaign days and spend are counted from 12:00 AM to 11:59 PM in {timeZone}, so everyone sees the same numbers."
              description="Tooltip on the opening's time zone chip. {timeZone} is an IANA zone such as America/Chicago"
              values={{ timeZone }}
            />
          </p>
        </div>
      }
    >
      <button
        type="button"
        className="max-w-full cursor-help rounded-full text-left"
        data-testid="time-zone-chip"
      >
        {/* Common zones fit on one line; a longer one wraps inside the fixed column. */}
        <Tag
          className="border-fx-neutral-200 h-auto min-h-6 max-w-full border bg-white whitespace-normal"
          textClassName="min-w-0"
          icon={<Clock aria-hidden className="text-fx-neutral-500 size-3" />}
          text={children}
        />
      </button>
    </Tooltip>
  );
}

/** The zone inside a chip's text, set apart from the words around it. */
export function TimeZoneChipZone({ children }: { children: ReactNode }) {
  return (
    <Typography as="span" variant="small2" className="whitespace-nowrap">
      {children}
    </Typography>
  );
}
